import { combineReducers, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { normalize, schema } from 'normalizr';
import {
    ResourceType,
    SaveUserGroupDto,
    SetRecordPermissionDto,
    UserGroupDto,
    UserGroupPermissionDto
} from '../../../common/PricePrgClient';
import { createAsyncAction } from '../../../utils';
import { userRemoved } from '../../user/redux/userSlices';

const namespaces = {
    userGroups: 'userGroups',
    selectedUserGroupId: 'selectedUserGroupId'
};

const userGroupPermissionEntity = new schema.Entity<UserGroupPermissionDto>(
    'userGroupPermissions',
    undefined,
    {
        idAttribute: 'userGroupPermissionId'
    }
);

const userGroupEntity = new schema.Entity<UserGroup>(
    'userGroups',
    {
        permissions: [userGroupPermissionEntity]
    },
    {
        idAttribute: 'userGroupId'
    }
);

export type UserGroup = {
    userGroupId: number;
    name: string;
    description: string;
    disableAgreementEmail: boolean;
    hideWarehouseBalance: boolean;
    members: number[];
    permissions: number[];
};

export type UserGroups = {
    groups: { [userGroupId: string]: UserGroup };
    permissions: { [userGroupPermissionId: string]: UserGroupPermissionDto };
};

export type AddMembersToGroupActionPayload = { userGroupId: number; userIds: number[] };
export type RemoveMembersFromGroupActionPayload = { userGroupId: number; userId: number };
export type SetGroupPagePermissionActionPayload = {
    userGroupId: number;
    resourceType: ResourceType;
    hasAccess: boolean;
};

export const fetchUserGroups = createAsyncAction<void, UserGroupDto[]>(
    namespaces.userGroups,
    'fetchUserGroups'
);
export const createUserGroup = createAsyncAction<SaveUserGroupDto, UserGroupDto>(
    namespaces.userGroups,
    'createUserGroup'
);
export const addMembersToGroup = createAsyncAction<
    AddMembersToGroupActionPayload,
    AddMembersToGroupActionPayload
>(namespaces.userGroups, 'addMembersToGroupRequest');

export const removeMemberFromGroup = createAsyncAction<
    RemoveMembersFromGroupActionPayload,
    RemoveMembersFromGroupActionPayload
>(namespaces.userGroups, 'removeMemberFromGroup');

export const removeUserGroup = createAsyncAction<number, number>(namespaces.userGroups, 'removeUserGroup');

export const updateUserGroup = createAsyncAction<
    { userGroupId: number; dto: SaveUserGroupDto },
    { userGroupId: number; dto: SaveUserGroupDto }
>(namespaces.userGroups, 'updateUserGroup');

export const userGroupsSlice = createSlice({
    name: namespaces.userGroups,
    initialState: { groups: {}, permissions: {} } as UserGroups,
    reducers: {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        setRecordPermissionForGroupRequest: (state, action: PayloadAction<SetRecordPermissionDto>) => state,
        setPricelistRecordPermissionsForGroupRequest: (
            state,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            action: PayloadAction<SetRecordPermissionDto & { applyToChildren: boolean }>
        ) => state,
        setPagePermissionForGroupRequest: (
            state,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            action: PayloadAction<SetGroupPagePermissionActionPayload>
        ) => state,
        setPermissionForGroupSuccess: (state, action: PayloadAction<UserGroupPermissionDto>) => {
            const { payload } = action;

            const permissions = state.groups[payload.userGroupId]?.permissions;
            if (permissions == null) {
                return;
            }

            if (!permissions.includes(payload.userGroupPermissionId)) {
                permissions.push(payload.userGroupPermissionId);
            }

            state.permissions[payload.userGroupPermissionId] = payload;
        },
        setPermissionsForGroupSuccess: (state, action: PayloadAction<UserGroupPermissionDto[]>) => {
            const { payload } = action;

            for (const userGroupPermission of payload) {
                const permissions = state.groups[userGroupPermission.userGroupId].permissions;
                if (!permissions.includes(userGroupPermission.userGroupPermissionId)) {
                    permissions.push(userGroupPermission.userGroupPermissionId);
                }

                state.permissions[userGroupPermission.userGroupPermissionId] = userGroupPermission;
            }
        }
    },
    extraReducers: builder =>
        builder
            .addCase(userRemoved, (state, action) => {
                const userId = action.payload;
                for (const group of Object.values(state.groups)) {
                    const idx = group.members.indexOf(userId);
                    if (idx !== -1) {
                        group.members.splice(idx, 1);
                    }
                }
            })
            .addCase(fetchUserGroups.success, (state, action) => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const normalized: any = normalize(action.payload, [userGroupEntity]);

                return {
                    groups: normalized.entities.userGroups ?? {},
                    permissions: normalized.entities.userGroupPermissions ?? {}
                };
            })
            .addCase(createUserGroup.success, (state, action) => {
                const groups = normalize(action.payload, userGroupEntity).entities.userGroups ?? {};

                for (const [groupId, group] of Object.entries(groups)) {
                    state.groups[groupId] = group;
                }
            })
            .addCase(addMembersToGroup.success, (state, action) => {
                const { payload } = action;

                const group = state.groups[payload.userGroupId];
                if (group != null) {
                    group.members.push(...payload.userIds);
                }
            })
            .addCase(removeMemberFromGroup.success, (state, action) => {
                const { payload } = action;

                const group = state.groups[payload.userGroupId];
                if (group != null) {
                    group.members.splice(group.members.indexOf(payload.userId), 1);
                }
            })
            .addCase(removeUserGroup.success, (state, action) => {
                for (const userGroupPermissionId of state.groups[action.payload].permissions) {
                    delete state.permissions[userGroupPermissionId];
                }
                delete state.groups[action.payload];
            })
            .addCase(updateUserGroup.success, (state, action) => {
                const { payload } = action;

                const group = state.groups[payload.userGroupId];
                if (group != null) {
                    group.name = payload.dto.name;
                    group.description = payload.dto.description ?? '';
                    group.hideWarehouseBalance = payload.dto.hideWarehouseBalance;
                    group.disableAgreementEmail = payload.dto.disableInactivateArticleEmails;
                }
            })
});

export const {
    setRecordPermissionForGroupRequest,
    setPricelistRecordPermissionsForGroupRequest,
    setPagePermissionForGroupRequest,
    setPermissionForGroupSuccess,
    setPermissionsForGroupSuccess
} = userGroupsSlice.actions;

export const selectedUserGroupIdSlice = createSlice({
    name: namespaces.selectedUserGroupId,
    initialState: null as number | null,
    reducers: {
        userGroupSelected: (state, action: PayloadAction<number>) => action.payload
    }
});
export const { userGroupSelected } = selectedUserGroupIdSlice.actions;

export default combineReducers({
    userGroups: userGroupsSlice.reducer,
    ui: combineReducers({
        selectedUserGroupId: selectedUserGroupIdSlice.reducer
    })
});
